/**
 * Utilities for managing metrics created before authentication
 * These are stored in localStorage and transferred to the user's account upon signup
 * Also optionally saved to backend with email association for cross-device persistence
 */

import { supabase } from "@/integrations/supabase/client";

export interface PreAuthMetric {
  key: string;
  name: string;
  emoji: string;
  unit: string;
  type: "numeric" | "boolean";
  goal: number;
  inverse: boolean;
  isRelative?: boolean;
  created_at: string;
}

const STORAGE_KEY = "gitfit_preauth_metrics";
const GENERATION_COUNT_KEY = "gitfit_preauth_generation_count";
const GENERATION_DATE_KEY = "gitfit_preauth_generation_date";
const EMAIL_STORAGE_KEY = "gitfit_preauth_email";

/**
 * Save metrics created before authentication
 */
export function savePreAuthMetrics(metrics: PreAuthMetric[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(metrics));
  } catch (error) {
    console.error("[PreAuthMetrics] Failed to save metrics:", error);
  }
}

/**
 * Load metrics created before authentication
 */
export function loadPreAuthMetrics(): PreAuthMetric[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    return JSON.parse(stored);
  } catch (error) {
    console.error("[PreAuthMetrics] Failed to load metrics:", error);
    return [];
  }
}

/**
 * Clear pre-auth metrics (called after successful transfer to user account)
 */
export function clearPreAuthMetrics(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error("[PreAuthMetrics] Failed to clear metrics:", error);
  }
}

/**
 * Check if user has any pre-auth metrics
 */
export function hasPreAuthMetrics(): boolean {
  return loadPreAuthMetrics().length > 0;
}

/**
 * Get pending HealthKit metrics that need notification
 * Returns array of metric names that were selected during onboarding
 */
export function getPendingHealthKitMetrics(): string[] {
  try {
    const stored = localStorage.getItem("gitfit_healthkit_metrics_pending");
    if (!stored) return [];
    return JSON.parse(stored);
  } catch (error) {
    console.error("[PreAuthMetrics] Failed to get pending HealthKit metrics:", error);
    return [];
  }
}

/**
 * Clear pending HealthKit metrics flag (after showing notification)
 */
export function clearPendingHealthKitMetrics(): void {
  try {
    localStorage.removeItem("gitfit_healthkit_metrics_pending");
  } catch (error) {
    console.error("[PreAuthMetrics] Failed to clear pending HealthKit metrics:", error);
  }
}

/**
 * Rate limiting for anonymous metric generation
 * Returns true if rate limit exceeded
 */
export function checkRateLimit(): { exceeded: boolean; count: number; maxCount: number } {
  const MAX_REQUESTS_PER_DAY = 10;
  
  try {
    const today = new Date().toDateString();
    const storedDate = localStorage.getItem(GENERATION_DATE_KEY);
    const storedCount = localStorage.getItem(GENERATION_COUNT_KEY);
    
    // Reset count if it's a new day
    if (storedDate !== today) {
      localStorage.setItem(GENERATION_DATE_KEY, today);
      localStorage.setItem(GENERATION_COUNT_KEY, "0");
      return { exceeded: false, count: 0, maxCount: MAX_REQUESTS_PER_DAY };
    }
    
    const count = parseInt(storedCount || "0", 10);
    
    return {
      exceeded: count >= MAX_REQUESTS_PER_DAY,
      count,
      maxCount: MAX_REQUESTS_PER_DAY
    };
  } catch (error) {
    console.error("[PreAuthMetrics] Failed to check rate limit:", error);
    return { exceeded: false, count: 0, maxCount: MAX_REQUESTS_PER_DAY };
  }
}

/**
 * Increment rate limit counter
 */
export function incrementRateLimit(): void {
  try {
    const today = new Date().toDateString();
    const storedDate = localStorage.getItem(GENERATION_DATE_KEY);
    const storedCount = localStorage.getItem(GENERATION_COUNT_KEY);
    
    // Reset count if it's a new day
    if (storedDate !== today) {
      localStorage.setItem(GENERATION_DATE_KEY, today);
      localStorage.setItem(GENERATION_COUNT_KEY, "1");
      return;
    }
    
    const count = parseInt(storedCount || "0", 10);
    localStorage.setItem(GENERATION_COUNT_KEY, String(count + 1));
  } catch (error) {
    console.error("[PreAuthMetrics] Failed to increment rate limit:", error);
  }
}

/**
 * Generate a stable browser fingerprint for rate limiting
 * Used as fallback when localStorage is not available (iOS Safari, private browsing)
 */
function generateBrowserFingerprint(): string {
  const nav = window.navigator;
  const screen = window.screen;
  
  // Collect stable browser characteristics
  const components = [
    nav.userAgent || '',
    nav.language || '',
    screen.colorDepth || '',
    screen.width || '',
    screen.height || '',
    new Date().getTimezoneOffset().toString(),
    // Session storage test - more likely to work than localStorage on iOS
    (() => {
      try {
        return sessionStorage.getItem('fp_test') || 'none';
      } catch {
        return 'blocked';
      }
    })(),
  ];
  
  // Create a simple hash of the components
  const fingerprint = components.join('|');
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return `fp_${Math.abs(hash).toString(36)}`;
}

/**
 * Get a unique device/session identifier
 * Used for rate limiting anonymous requests
 */
export function getDeviceId(): string {
  const DEVICE_ID_KEY = "gitfit_device_id";
  
  // Try localStorage first
  try {
    let deviceId = localStorage.getItem(DEVICE_ID_KEY);
    if (!deviceId) {
      deviceId = `device_${Date.now()}_${Math.random().toString(36).substring(7)}`;
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    }
    return deviceId;
  } catch (localStorageError) {
    console.warn("[PreAuthMetrics] localStorage unavailable, using fingerprint fallback");
  }
  
  // Fallback 1: Try sessionStorage (more permissive on iOS)
  try {
    const SESSION_DEVICE_KEY = "gitfit_session_device_id";
    let sessionDeviceId = sessionStorage.getItem(SESSION_DEVICE_KEY);
    if (!sessionDeviceId) {
      // Use fingerprint + session marker for stability within the session
      sessionDeviceId = `session_${generateBrowserFingerprint()}_${Date.now()}`;
      sessionStorage.setItem(SESSION_DEVICE_KEY, sessionDeviceId);
    }
    return sessionDeviceId;
  } catch (sessionStorageError) {
    console.warn("[PreAuthMetrics] sessionStorage also unavailable, using pure fingerprint");
  }
  
  // Fallback 2: Use stable browser fingerprint (no storage)
  // This ensures rate limiting still works even when all storage is blocked
  return generateBrowserFingerprint();
}

/**
 * Save email for associating pre-auth metrics with a user
 * This allows metrics to persist across devices/sessions
 */
export function savePreAuthEmail(email: string): void {
  try {
    localStorage.setItem(EMAIL_STORAGE_KEY, email.toLowerCase());
  } catch (error) {
    console.error("[PreAuthMetrics] Failed to save email:", error);
  }
}

/**
 * Get saved email for pre-auth metrics
 */
export function getPreAuthEmail(): string | null {
  try {
    return localStorage.getItem(EMAIL_STORAGE_KEY);
  } catch (error) {
    console.error("[PreAuthMetrics] Failed to get email:", error);
    return null;
  }
}

/**
 * Clear saved email
 */
export function clearPreAuthEmail(): void {
  try {
    localStorage.removeItem(EMAIL_STORAGE_KEY);
  } catch (error) {
    console.error("[PreAuthMetrics] Failed to clear email:", error);
  }
}

/**
 * Save pre-auth metrics to backend (with email association)
 * This allows metrics to persist across devices for up to 7 days
 */
export async function savePreAuthMetricsToBackend(email: string, metrics: PreAuthMetric[]): Promise<boolean> {
  try {
    if (!email || metrics.length === 0) {
      return false;
    }

    console.log(`[PreAuthMetrics] Saving ${metrics.length} metrics to backend for ${email}`);

    const { data, error } = await supabase.functions.invoke("save-preauth-metrics", {
      body: {
        email: email.toLowerCase(),
        metrics,
        deviceFingerprint: getDeviceId(),
      },
    });

    if (error) {
      console.error("[PreAuthMetrics] Failed to save to backend:", error);
      return false;
    }

    console.log("[PreAuthMetrics] Successfully saved to backend:", data);
    
    // Save the email locally for later retrieval
    savePreAuthEmail(email);
    
    return true;
  } catch (error) {
    console.error("[PreAuthMetrics] Error saving to backend:", error);
    return false;
  }
}

/**
 * Fetch pre-auth metrics from backend (requires authentication)
 * Returns metrics that were saved with the user's email before they signed up
 */
export async function fetchPreAuthMetricsFromBackend(): Promise<PreAuthMetric[]> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.log("[PreAuthMetrics] No session, cannot fetch from backend");
      return [];
    }

    console.log("[PreAuthMetrics] Fetching metrics from backend");

    const { data, error } = await supabase.functions.invoke("fetch-preauth-metrics", {
      headers: { Authorization: `Bearer ${session?.access_token}` },
    });

    if (error) {
      console.error("[PreAuthMetrics] Failed to fetch from backend:", error);
      return [];
    }

    const metrics = data?.metrics || [];
    console.log(`[PreAuthMetrics] Fetched ${metrics.length} metrics from backend`);
    
    return metrics;
  } catch (error) {
    console.error("[PreAuthMetrics] Error fetching from backend:", error);
    return [];
  }
}

/**
 * Mark backend metrics as transferred
 */
export async function markBackendMetricsAsTransferred(email: string): Promise<void> {
  try {
    // This is handled automatically by the backend when metrics are fetched
    // and then transferred - no need for explicit marking
    console.log(`[PreAuthMetrics] Metrics for ${email} will be marked as transferred by backend`);
  } catch (error) {
    console.error("[PreAuthMetrics] Error marking metrics as transferred:", error);
  }
}
